import React from 'react';
import { Grid, SelectChangeEvent, Typography } from '@mui/material';
import { useGlobalization } from '@Translations';
import type { CostTableSummary } from '@Types/cost.types';
import { CostApplication } from '@Types/rehab.types';
import type { DefectCode } from '@Types/utils.types';
import { TrenchDefectFields } from './TrenchCalculation/TrenchDefectFields';
import { getCostApplicationOptions } from './costApplicationOptions';
import { LabeledSelect } from './LabeledSelect';

interface CostTableFieldsProps {
	costTables: CostTableSummary[];
	selectedCostTableId: string;
	onSelect: (id: string) => void;
	summary: string | null;
	trenchCalculationEnabled: boolean;
	system: 'Metric' | 'Imperial';
	selectedCostApplication: CostApplication;
	onSelectCostApplication: (application: CostApplication) => void;
	defectCodes: string[];
	defectCodeOptions: DefectCode[];
	defectCodesLoading?: boolean;
	onDefectCodesChange: (codes: string[]) => void;
}

export const CostTableFields = ({
	costTables,
	selectedCostTableId,
	onSelect,
	summary,
	trenchCalculationEnabled,
	system,
	selectedCostApplication,
	onSelectCostApplication,
	defectCodes,
	defectCodeOptions,
	defectCodesLoading,
	onDefectCodesChange,
}: CostTableFieldsProps) => {
	const { t } = useGlobalization();
	const costApplicationOptions = getCostApplicationOptions(t, system);

	return (
		<>
			<Grid xs={trenchCalculationEnabled ? 6 : 12} item>
				<LabeledSelect
					label={t('Cost Table')}
					value={selectedCostTableId}
					dataCy="cost-table-select"
					name="CostTable"
					options={costTables.map(ct => ({
						value: ct.id,
						label: ct.name,
					}))}
					onChange={(event: SelectChangeEvent<string>) =>
						onSelect(event.target.value)
					}
				/>
			</Grid>
			{trenchCalculationEnabled && (
				<Grid xs={6} item>
					<LabeledSelect
						label={t('Apply to')}
						value={selectedCostApplication}
						dataCy="cost-application-select"
						name="CostApplication"
						options={costApplicationOptions}
						onChange={(event: SelectChangeEvent<string>) =>
							onSelectCostApplication(
								event.target.value as CostApplication,
							)
						}
					/>
				</Grid>
			)}
			{summary && (
				<Grid xs={12} item>
					<Typography
						variant="body2"
						data-testid="cost-table-summary"
						data-cy="cost-table-summary"
						color="textSecondary">
						{summary}
					</Typography>
				</Grid>
			)}
			{trenchCalculationEnabled &&
				selectedCostApplication === CostApplication.trenchLength && (
					<TrenchDefectFields
						system={system}
						defectCodes={defectCodes}
						defectCodeOptions={defectCodeOptions}
						defectCodesLoading={defectCodesLoading}
						onDefectCodesChange={onDefectCodesChange}
					/>
				)}
		</>
	);
};
